import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { distinctDueDates, sharedDescription, type SchoolInvoiceTableRow } from './invoiceRules';
import { useUpdateSchoolDrafts, type UpdateSchoolDraftsPayload } from './useUpdateSchoolDrafts';

interface EditSchoolDraftsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolYearId: string;
  /** The whole selection; the backend skips whatever is not a draft. */
  invoices: readonly SchoolInvoiceTableRow[];
  onCompleted: (payload: UpdateSchoolDraftsPayload) => void;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function EditSchoolDraftsDialog({
  open,
  onOpenChange,
  schoolYearId,
  invoices,
  onCompleted,
}: EditSchoolDraftsDialogProps) {
  const drafts = useMemo(() => invoices.filter((row) => row.status === 'DRAFT'), [invoices]);
  const skippedCount = invoices.length - drafts.length;
  const prefill = useMemo(() => sharedDescription(drafts) ?? '', [drafts]);
  const dueDateCount = useMemo(() => distinctDueDates(drafts).length, [drafts]);

  const [description, setDescription] = useState(prefill);
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [updateSchoolDrafts, { loading }] = useUpdateSchoolDrafts();

  useEffect(() => {
    if (!open) return;
    setDescription(prefill);
    setDueDate('');
    setError(null);
  }, [open, prefill]);

  const trimmedDescription = description.trim();
  const descriptionChanged = trimmedDescription !== '' && trimmedDescription !== prefill;
  const canSubmit = drafts.length > 0 && (descriptionChanged || dueDate !== '');

  const handleConfirm = async () => {
    setError(null);
    const response = await updateSchoolDrafts({
      variables: {
        input: {
          schoolYearId,
          invoiceIds: invoices.map((row) => row.invoiceId),
          ...(descriptionChanged ? { description: trimmedDescription } : {}),
          ...(dueDate ? { dueDate } : {}),
        },
      },
    });
    const payload = response.data?.updateSchoolDrafts;
    if (!payload) {
      setError('The update failed. Nothing was changed.');
      return;
    }
    // A refusal changes nothing and names why; a partial run has families to show.
    if (payload.families.length === 0) {
      setError(payload.userErrors?.[0]?.message ?? 'The update failed. Nothing was changed.');
      return;
    }
    onCompleted(payload);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!loading) onOpenChange(next); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {plural(drafts.length, 'draft', 'drafts')}</DialogTitle>
          <DialogDescription>
            {skippedCount > 0 && (
              <>
                {drafts.length} of the {plural(invoices.length, 'selected row', 'selected rows')}{' '}
                {drafts.length === 1 ? 'is a draft' : 'are drafts'}; the other {skippedCount} will be skipped.{' '}
              </>
            )}
            Only the description and the due date change here; amounts and line items are never changed in bulk.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-drafts-description">Description</Label>
            <Input
              id="edit-drafts-description"
              value={description}
              placeholder="New description"
              onChange={(event) => setDescription(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Replaces the description in each draft&apos;s memo and in the line items that carry it; installment and
              student labels stay as they are.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-drafts-due-date">Due date</Label>
            <Input
              id="edit-drafts-due-date"
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
            />
            {dueDateCount > 1 && (
              <p className="text-sm text-amber-700">
                These drafts have {dueDateCount} different due dates; setting one here applies it to all of them
              </p>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={loading || !canSubmit}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? 'Updating...' : `Update ${plural(drafts.length, 'draft', 'drafts')}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
